import { createHttpClient } from '@adapters/HttpClient';
import { ShopsApiAdapter } from '@modules/Shops/List/adapters/ShopsApiAdapter';
import { useOrders } from '@modules/Orders/List/hooks/useOrders';
import type { Order } from '@modules/Orders/List/models/Orders';
import { Input } from '@shared/components/ui/input';
import type { LoadOptionsResponse } from '@shared/components/ui/MultiSelect';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { apiClientWH } from '@shared/services';
import { cn } from '@shared/utils/utils';
import { Check, Loader2, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type ShippingRuleOrderSelectProps = {
  teamId: number;
  orderId: number | null;
  onSelect: (order: Order) => void;
  disabled?: boolean;
  dataTestId?: string;
};

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="min-w-0 space-y-1.5">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    {children}
  </div>
);

/**
 * Buscador por ref (input siempre visible) + `MultiSelect` de tiendas alineados en fila, y resultados en lista.
 */
export const ShippingRuleOrderSelect = ({
  teamId,
  orderId,
  onSelect,
  disabled = false,
  dataTestId = 'shipping-rule-order-select',
}: ShippingRuleOrderSelectProps) => {
  const { t } = useTranslation('rulesModule');
  const { t: tOrders } = useTranslation('ordersModule');

  const [refInput, setRefInput] = useState('');
  const debouncedRef = useDebouncedValue(refInput.trim(), 400);
  const [refForApi, setRefForApi] = useState('');
  const [shopIds, setShopIds] = useState<string[]>([]);

  useEffect(() => {
    setRefForApi(debouncedRef);
  }, [debouncedRef]);

  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(false);

  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const shopsAdapter = useMemo(() => new ShopsApiAdapter(httpClient), [httpClient]);

  const loadShopsForMultiSelect = useCallback(
    async (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      if (teamId <= 0) {
        return { options: [], hasMore: false };
      }
      try {
        const shopsPage = await shopsAdapter.getShopsPaginated({
          team_id: teamId,
          searchTerm: params.search,
          page: params.page,
          limit: params.limit,
          // API /shops solo admite `order_by` permitidos; `name` devuelve error 422
          sortBy: 'id',
          sortDirection: 'asc',
        });
        const options = shopsPage.shops.map((shop) => ({
          value: String(shop.id),
          label: shop.name,
        }));
        return {
          options,
          hasMore: shopsPage.hasMore,
        };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [shopsAdapter, teamId],
  );

  const searchFilter = useMemo(() => {
    const filters: { column: string; operator: string; value: string | string[] }[] = [
      { column: 'team_id', operator: 'in', value: [String(teamId)] },
    ];
    if (shopIds.length > 0) {
      filters.push({ column: 'shop_id', operator: 'in', value: shopIds });
    }
    return { filters, logic: 'AND' as const };
  }, [teamId, shopIds]);

  const ordersQueryFilters = useMemo(
    () => ({
      search: refForApi || undefined,
      searchFilter,
      limit: 30,
      sortBy: 'order_created_at',
      sortDirection: 'desc' as const,
    }),
    [refForApi, searchFilter],
  );

  const shouldLoad = teamId > 0;
  const { orders, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useOrders(ordersQueryFilters, {
    enabled: shouldLoad,
  });

  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage || isLoadingMoreRef.current) {
      return;
    }
    isLoadingMoreRef.current = true;
    void fetchNextPage().finally(() => {
      isLoadingMoreRef.current = false;
    });
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (!listRef.current || !sentinelRef.current) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          loadMore();
        }
      },
      { root: listRef.current, rootMargin: '100px', threshold: 0 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore, orders.length]);

  return (
    <div className="space-y-3" data-testid={dataTestId}>
      <div className="overflow-hidden rounded-lg border border-border">
        <div className="space-y-3 border-b border-border bg-muted/20 p-3">
          <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 sm:gap-4">
            <FilterField label={t('clientList.preview.orderSelect.refLabel')}>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  type="search"
                  value={refInput}
                  onChange={(e) => setRefInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setRefForApi(refInput.trim());
                    }
                  }}
                  placeholder={t('clientList.preview.orderSelect.refPlaceholder')}
                  disabled={disabled || teamId <= 0}
                  autoComplete="off"
                  data-testid={`${dataTestId}-ref-input`}
                  className="h-[38px] w-full min-w-0 pl-9"
                />
              </div>
            </FilterField>
            <FilterField label={t('clientList.preview.orderSelect.shopLabel')}>
              <MultiSelect
                loadOptions={loadShopsForMultiSelect}
                value={shopIds}
                onValueChange={setShopIds}
                placeholder={t('clientList.preview.orderSelect.shopAll')}
                emptyText={tOrders('filters.notFound', 'Sin resultados')}
                noResultsText={tOrders('filters.notFound', 'Sin resultados')}
                searchPlaceholder={t('clientList.preview.orderSelect.shopSearchPlaceholder')}
                dataTestId={`${dataTestId}-shop`}
                triggerClassName="h-[38px] w-full"
                pageSize={25}
                disabled={disabled || teamId <= 0}
              />
            </FilterField>
          </div>
        </div>

        <div ref={listRef} className="max-h-[min(40vh,320px)] overflow-y-auto">
          {isLoading && orders.length === 0 ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : orders.length === 0 ? (
            <div className="px-3 py-10 text-center text-sm text-muted-foreground">
              {t('clientList.preview.orderSelect.empty')}
            </div>
          ) : (
            <>
              {orders.map((order) => {
                const isSelected = order.id === orderId;
                return (
                  <button
                    key={order.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => onSelect(order)}
                    className={cn(
                      'flex w-full items-start gap-2 border-b border-border/60 px-3 py-2.5 text-left text-sm transition-colors last:border-0',
                      'hover:bg-muted/50',
                      isSelected && 'bg-muted/40',
                      disabled && 'pointer-events-none opacity-50',
                    )}
                  >
                    <div className="flex w-4 shrink-0 justify-center pt-0.5">
                      {isSelected ? <Check className="h-4 w-4 text-primary" /> : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-foreground">{order.ref}</div>
                      <div className="truncate text-xs text-muted-foreground">{order.shop?.name ?? '—'}</div>
                    </div>
                  </button>
                );
              })}
              <div ref={sentinelRef} className="h-1" />
              {isFetchingNextPage && (
                <div className="flex justify-center py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
